import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Clock, CheckCircle2, ArrowLeft, Maximize2, X } from 'lucide-react';

const ImageLightbox = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0A0A14]/90 backdrop-blur-md" />
      {/* Close button */}
      <button
        className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full bg-[#18181B] border border-[#7B61FF]/30 flex items-center justify-center text-[#F0EFF4]/70 hover:text-[#F0EFF4] hover:border-[#7B61FF]/70 transition-all"
        onClick={onClose}
      >
        <X className="w-5 h-5" />
      </button>
      {/* Image */}
      <img
        src={src}
        alt={alt}
        className="relative z-10 max-w-full max-h-[85vh] rounded-[1.5rem] shadow-[0_0_60px_rgba(123,97,255,0.25)] border border-[#7B61FF]/25 object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

gsap.registerPlugin(ScrollTrigger);

/**
 * PhasePageTemplate — shared layout for all 6 process phase sub-pages.
 *
 * Props:
 *   phaseNumber    string  e.g. '01'
 *   phaseName      string  e.g. 'The Discovery Form'
 *   tagline        string  one-line description
 *   heroImage      string  path to hero image (from /images/generated/)
 *   conceptImage   string  path to concept illustration
 *   delivers       array   [{ icon: <JSX />, title: string, desc: string }]
 *   whyTitle       string  headline for "Why We Do It This Way"
 *   whyBody        string  paragraph copy
 *   experienceTitle string headline for "What This Looks Like For You"
 *   experienceBody  string paragraph copy
 *   experienceHighlights array [string] optional bullet list
 *   timeline       string  e.g. '20–30 minutes'
 *   todoItems      array   [string]  what the client actually needs to do
 *   mockupImage    string  optional — path to a product screenshot/mockup image
 *   mockupCaption  string  optional — caption shown beneath the mockup
 *   prevPhase      object  { name, href } or null
 *   nextPhase      object  { name, href } or null
 */
const PhasePageTemplate = ({
  phaseNumber,
  phaseName,
  tagline,
  heroImage,
  conceptImage,
  delivers,
  whyTitle,
  whyBody,
  experienceTitle,
  experienceBody,
  experienceHighlights,
  timeline,
  todoItems,
  mockupImage,
  mockupCaption,
  prevPhase,
  nextPhase,
}) => {
  const containerRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.phase-hero-content > *',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.9, stagger: 0.12, ease: 'power3.out' }
      );

      gsap.utils.toArray('.reveal-up').forEach((el) => {
        gsap.fromTo(
          el,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.7,
            ease: 'power2.out',
            scrollTrigger: { trigger: el, start: 'top 88%' },
          }
        );
      });

      gsap.utils.toArray('.deliver-card').forEach((card, i) => {
        gsap.fromTo(
          card,
          { y: 25, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            ease: 'power2.out',
            delay: i * 0.1,
            scrollTrigger: { trigger: card, start: 'top 90%' },
          }
        );
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div
      className="bg-[#0A0A14] text-[#F0EFF4] min-h-screen selection:bg-[#7B61FF] selection:text-[#F0EFF4]"
      ref={containerRef}
    >
      {/* Hero */}
      <div className="relative min-h-[70vh] flex items-end pb-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${heroImage}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A14] via-[#0A0A14]/65 to-[#0A0A14]/20" />
        <div className="relative max-w-5xl mx-auto px-6 w-full phase-hero-content">
          <Link
            to="/process"
            className="inline-flex items-center gap-2 text-[#7B61FF]/70 text-sm font-semibold mb-6 hover:text-[#7B61FF] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Our Process
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-semibold tracking-wider text-[#7B61FF] mb-5 font-data">
            PHASE {phaseNumber}
          </div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-5 leading-tight">
            {phaseName.split(' ').slice(0, -1).join(' ')}{' '}
            <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal">
              {phaseName.split(' ').slice(-1)[0]}.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-[#F0EFF4]/70 max-w-2xl leading-relaxed">
            {tagline}
          </p>
        </div>
      </div>

      {/* What We Deliver */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        <div className="reveal-up mb-12">
          <span className="font-data text-xs font-bold tracking-widest text-[#7B61FF] uppercase">
            What We Deliver
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mt-3">
            Here's what you get.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {delivers.map((item, i) => (
            <div
              key={i}
              className="deliver-card bg-[#18181B] rounded-[2rem] p-8 border border-[#7B61FF]/15 hover:border-[#7B61FF]/40 hover:shadow-[0_0_25px_rgba(123,97,255,0.08)] transition-all duration-300"
            >
              <div className="w-12 h-12 bg-[#7B61FF]/10 rounded-xl flex items-center justify-center text-[#7B61FF] mb-5">
                {item.icon}
              </div>
              <h3 className="text-lg font-bold mb-3">{item.title}</h3>
              <p className="text-[#F0EFF4]/60 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Why We Do It This Way — image left, text right */}
      <div className="border-t border-[#7B61FF]/10 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className="reveal-up order-2 md:order-1">
              <img
                src={conceptImage}
                alt={`${phaseName} — concept`}
                className="w-full rounded-[2rem] object-cover aspect-video"
                loading="lazy"
              />
            </div>
            <div className="reveal-up order-1 md:order-2">
              <span className="font-data text-xs font-bold tracking-widest text-[#7B61FF] uppercase">
                Why We Do It This Way
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mt-3 mb-5">{whyTitle}</h2>
              <p className="text-[#F0EFF4]/65 leading-relaxed">{whyBody}</p>
            </div>
          </div>
        </div>
      </div>

      {/* What This Looks Like For You */}
      <div className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="reveal-up mb-8">
            <span className="font-data text-xs font-bold tracking-widest text-[#7B61FF] uppercase">
              What This Looks Like For You
            </span>
            <h2 className="text-2xl md:text-3xl font-bold mt-3 mb-5">
              {experienceTitle}
            </h2>
          </div>
          <div className="reveal-up bg-[#18181B] rounded-[2rem] p-10 border border-[#7B61FF]/15">
            <p className="text-[#F0EFF4]/70 leading-relaxed text-lg mb-6">{experienceBody}</p>
            {experienceHighlights && experienceHighlights.length > 0 && (
              <ul className="space-y-3 mt-6">
                {experienceHighlights.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#F0EFF4]/70 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-[#7B61FF] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Product Mockup — optional, click to expand */}
      {mockupImage && (
        <div className="py-10">
          <div className="max-w-5xl mx-auto px-6">
            <div className="reveal-up">
              <div
                className="relative max-w-sm mx-auto group cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}
              >
                <img
                  src={mockupImage}
                  alt={mockupCaption || 'Product screenshot'}
                  className="w-full rounded-[1.5rem] shadow-[0_0_40px_rgba(123,97,255,0.15)] border border-[#7B61FF]/20 group-hover:border-[#7B61FF]/50 group-hover:shadow-[0_0_55px_rgba(123,97,255,0.25)] transition-all duration-300"
                  loading="lazy"
                />
                {/* Expand hint */}
                <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#0A0A14]/70 backdrop-blur-sm border border-[#7B61FF]/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <Maximize2 className="w-3.5 h-3.5 text-[#7B61FF]" />
                </div>
              </div>
              {mockupCaption && (
                <p className="text-center text-xs font-data text-[#F0EFF4]/35 mt-4 uppercase tracking-widest">
                  {mockupCaption}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && mockupImage && (
        <ImageLightbox
          src={mockupImage}
          alt={mockupCaption || 'Product screenshot'}
          onClose={closeLightbox}
        />
      )}

      {/* Timeline + What You Need To Do — side by side */}
      <div className="py-10 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Timeline */}
            <div className="reveal-up bg-[#18181B] rounded-[2rem] p-8 border border-[#7B61FF]/15">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 text-[#7B61FF]" />
                <span className="font-data text-xs font-bold tracking-widest text-[#7B61FF] uppercase">
                  Timeline
                </span>
              </div>
              <p className="text-3xl font-bold">{timeline}</p>
              <p className="text-[#F0EFF4]/50 text-sm mt-2">
                From your perspective — we build in the time it takes.
              </p>
            </div>
            {/* What You Need To Do */}
            <div className="reveal-up bg-[#18181B] rounded-[2rem] p-8 border border-[#7B61FF]/15">
              <span className="font-data text-xs font-bold tracking-widest text-[#7B61FF] uppercase">
                What You Need To Do
              </span>
              <ul className="mt-5 space-y-3">
                {todoItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#F0EFF4]/70 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-[#7B61FF] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Phase Navigation */}
      <div className="border-t border-[#7B61FF]/10 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-stretch gap-4 justify-between">
            {prevPhase ? (
              <Link
                to={prevPhase.href}
                className="reveal-up flex-1 flex items-center gap-4 bg-[#18181B] rounded-[2rem] p-6 border border-[#7B61FF]/15 hover:border-[#7B61FF]/40 transition-all group"
              >
                <ArrowLeft className="w-5 h-5 text-[#7B61FF] flex-shrink-0 group-hover:-translate-x-1 transition-transform" />
                <div>
                  <p className="font-data text-[10px] text-[#F0EFF4]/40 uppercase tracking-widest mb-1">Previous</p>
                  <p className="font-semibold text-[#F0EFF4]/80">{prevPhase.name}</p>
                </div>
              </Link>
            ) : (
              <div className="flex-1" />
            )}
            {nextPhase ? (
              <Link
                to={nextPhase.href}
                className="reveal-up flex-1 flex items-center justify-end gap-4 bg-[#7B61FF]/10 border border-[#7B61FF]/30 rounded-[2rem] p-6 hover:bg-[#7B61FF]/20 transition-all group"
              >
                <div className="text-right">
                  <p className="font-data text-[10px] text-[#7B61FF]/60 uppercase tracking-widest mb-1">Next Phase</p>
                  <p className="font-semibold text-[#F0EFF4]">{nextPhase.name}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#7B61FF] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : (
              <div className="flex-1" />
            )}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-[#18181B]/50">
        <div className="max-w-xl mx-auto px-6 text-center reveal-up">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to get{' '}
            <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-3xl md:text-4xl">
              started?
            </span>
          </h2>
          <p className="text-[#F0EFF4]/60 mb-8">
            Reserve your build slot. We'll walk you through the rest.
          </p>
          <a
            href="https://buy.stripe.com/14A8wOgNKfKz3HedcT2kw07"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-10 py-5 rounded-full bg-[#7B61FF] text-[#F0EFF4] font-bold tracking-wide hover:-translate-y-1 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.6)]"
          >
            Reserve Your Build
          </a>
        </div>
      </div>
    </div>
  );
};

export default PhasePageTemplate;
